import { useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import type { DiagramViewDefinition } from '@/hooks/useDiagramInfo';
import type { OrgModelLensProjection } from '../models/OrgModelProjection';
import { isFullOrgLiveView } from '../models/orgArtifactKind';
import { useOrgModel } from './useOrgModel';

export interface AtlasInsightViewTitle {
  heading: string;
  subtitle: string;
}

export function resolveAtlasInsightViewTitle(input: {
  lensKey: string | undefined;
  analytics: OrgModelLensProjection['analytics'] | undefined;
  canonicalTitle: string;
  isFullOrgLiveView: boolean;
}): AtlasInsightViewTitle | null {
  if (!input.lensKey || !input.isFullOrgLiveView) return null;

  const insight = input.analytics?.find(
    (candidate) => candidate.key === input.lensKey,
  );
  return {
    heading: insight?.name ?? 'Insight view',
    subtitle: `Insight view of ${input.canonicalTitle}`,
  };
}

export function useAtlasInsightViewTitle(input: {
  diagramId: string | undefined;
  folderId: string | null | undefined;
  viewDefinition: DiagramViewDefinition | null | undefined;
  canonicalTitle: string;
}): AtlasInsightViewTitle | null {
  const search = useSearch({ strict: false }) as { lens?: string } | undefined;
  const lensKey = typeof search?.lens === 'string' && search.lens.trim()
    ? search.lens
    : undefined;
  const fullOrgLiveView = isFullOrgLiveView(input.viewDefinition);
  const orgModel = useOrgModel({
    folderId: input.folderId,
    diagramId: input.diagramId,
    enabled: Boolean(lensKey && fullOrgLiveView),
  });

  return useMemo(
    () => resolveAtlasInsightViewTitle({
      lensKey,
      analytics: orgModel.data?.lenses?.analytics,
      canonicalTitle: input.canonicalTitle,
      isFullOrgLiveView: fullOrgLiveView,
    }),
    [lensKey, fullOrgLiveView, input.canonicalTitle, orgModel.data?.lenses?.analytics],
  );
}
